import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  readConsent,
  writeConsent,
  type ConsentCategory,
  type ConsentPreferences,
} from './consent';
import {
  enablePostHogTracking,
  disablePostHogTracking,
  enableSessionReplay,
  disableSessionReplay,
} from './posthog';

interface ConsentContextValue {
  preferences: ConsentPreferences | null;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (categories: Record<ConsentCategory, boolean>) => void;
  openPreferences: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  // Initialize with SSR-safe defaults (no cookie access during server render).
  // useEffect corrects the state after hydration to match actual client storage.
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(null);
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    setPreferences(stored);
    setBannerVisible(stored === null);
  }, []);

  const applyConsent = useCallback(
    (categories: Record<ConsentCategory, boolean>) => {
      writeConsent(categories);
      setPreferences(readConsent());
      setBannerVisible(false);

      if (categories.analytics) {
        enablePostHogTracking();
      } else {
        disablePostHogTracking();
      }

      if (categories.sessionReplay) {
        enableSessionReplay();
      } else {
        disableSessionReplay();
      }
    },
    [],
  );

  const acceptAll = useCallback(() => {
    applyConsent({ analytics: true, sessionReplay: true });
  }, [applyConsent]);

  const rejectAll = useCallback(() => {
    applyConsent({ analytics: false, sessionReplay: false });
  }, [applyConsent]);

  const openPreferences = useCallback(() => {
    setBannerVisible(true);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      preferences,
      showBanner: bannerVisible,
      acceptAll,
      rejectAll,
      savePreferences: applyConsent,
      openPreferences,
    }),
    [preferences, bannerVisible, acceptAll, rejectAll, applyConsent, openPreferences],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
}
